import React from 'react';
import { styled } from '@mui/material/styles';
import {Box, Typography} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SettingsIcon from '@mui/icons-material/Settings';
import ErrorIcon from '@mui/icons-material/Error';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import { useTranslation } from "react-i18next";
import { useLocation } from 'react-router-dom';

const StatusContainer = styled(Box)({
  backgroundColor: 'white',
  padding: '16px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderTop: '1px solid #e0e0e0',
  marginTop: 'auto' 
});

const Title = styled(Typography)({
  marginRight: '24px',
  fontWeight: 'bold',
  fontSize: '1rem',
  color: '#333',
  minWidth: '120px'
});

const StepContainer = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
});

const StepCircle = styled(Box)(({ active, error }) => ({
  width: '25px',
  height: '25px',
  borderRadius: '50%',
  border: `2px solid ${error ? '#f44336' : active ? '#7b1fa2' : '#b39ddb'}`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: error ? '#f44336' : active ? '#7b1fa2' : '#b39ddb',
}));

const StepLine = styled(Box)(({ active, error }) => ({
  width: '60px',
  height: '2px',
  backgroundColor: error ? '#f44336' : active ? '#7b1fa2' : '#b39ddb',
  margin: '0 8px',
  marginTop: '15px',
}));

const StepText = styled(Typography)(({ active, error }) => ({
  marginTop: '8px',
  textAlign: 'center',
  color: error ? '#f44336' : active ? '#7b1fa2' : '#b39ddb',
  fontWeight: (active || error) ? 'bold' : 'normal',
  fontSize: '0.875rem',
}));

const Step = ({ icon, text, active, error }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <StepCircle active={active} error={error}>{icon}</StepCircle>
      <StepText active={active} error={error}>{text}</StepText>
    </Box>
  );
};

const CaseStatusStepper = ({ loading, process, caseResult, failStatus = false }) => {
  const { t } = useTranslation("global");
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const caseParam = searchParams.get('case');
  const isErrorFromUrl = caseParam && caseParam.startsWith('ERROR');
  const isError = isErrorFromUrl || failStatus;
  
  let activeStep = 0;
  
  if (loading) {
    activeStep = 1; 
  } else if (process) {
    activeStep = 2; 
  } else if (caseResult) {
    activeStep = 3; 
  }

  return (
    <StatusContainer>
      <Title variant="subtitle1">
        {t("caseStatus.title")}:
      </Title>
      <StepContainer>
        <Step 
          icon={<HomeIcon />} 
          text={t("caseStatus.start")} 
          active={activeStep === 0} 
        />
        <StepLine active={activeStep >= 1} error={false} />
        <Step 
          icon={<AutorenewIcon />} 
          text={t("caseStatus.loading")} 
          active={activeStep === 1} 
        />
        <StepLine active={activeStep >= 2} error={false} />
        <Step 
          icon={<SettingsIcon />} 
          text={t("caseStatus.processing")} 
          active={activeStep === 2} 
        />
        <StepLine active={activeStep >= 3 || isError} error={isError} />
        <Step 
          icon={isError ? <ErrorIcon /> : <SportsScoreIcon />} 
          text={isError ? t("caseStatus.error") : t("caseStatus.finished")} 
          active={activeStep === 3 && !isError} 
          error={isError}
        />
      </StepContainer>
    </StatusContainer>
  );
};

export default CaseStatusStepper;